// Generic
import React, { useEffect, useState, useRef } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useCustomers } from 'context/customers'

// Models
import { Customer, CustomerAssignment, CustomerEmployee } from 'models'

// Functions

// Components
import PopConfirm from 'antd/es/popconfirm'
import Input from 'antd/es/input'
import Form from 'antd/es/form'
import Select from 'antd/es/select'
import Content from 'components/content'
import Table, { Tr, Td, Th } from 'components/table'
import AddCustomerModal from './add-customer'
import Button from 'components/button'
import Notification from 'antd/es/notification'
import ModifyContactsModal from './modify-contacts'
import ModifyAssignmentsModal from './modify-assignments'

import DisplayCustomerAssignment from './display-assignment'
import DisplayCustomerContact from './display-contact'

import ModifyEmployees from './modify-employees'

import { useEmployees } from 'context/employees'

// Styles
import './style.scss'

const Customers = () => {
  const { data: customers, loading: customersLoading, refetch: refetchCustomers, createCustomer, updateCustomer, deleteCustomer } = useCustomers()

  const { data: employees, loading: employeesLoading, refetch: refetchEmployees, setQueryParamValues: employeesSetQueryParamValues } = useEmployees()

  const history = useHistory()

  // State
  const [showModifyContacts, setShowModifyContacts] = useState<boolean>(false)
  const [showModifyAssignments, setShowModifyAssignments] = useState<boolean>(false)
  const [showModifyEmployees, setShowModifyEmployees] = useState<boolean>(false)
  const [showAddCustomerModal, setShowAddCustomerModal] = useState<boolean>(false)
  const [form] = Form.useForm<Customer>()

  // Editable Item
  const [editableItem, _setEditableItem] = useState<Customer | undefined>(undefined)
  const refEditableItem = useRef<Customer | undefined>(undefined)
  const setEditableItem = (customer: Customer | undefined) => {
    refEditableItem.current = customer
    _setEditableItem(customer)
  }

  const location = useLocation()

  const getEditableItem = (): Customer | undefined => {
    return refEditableItem.current
  }

  const handleUpdateCustomer = async () => {
    const customer = refEditableItem.current
    if (customer) {
      try {
        await updateCustomer(customer)
        setEditableItem(undefined)
        form.resetFields()
        Notification.success({
          message: `Päivitettiin ${customer.name} (${customer.business_identity_code})`,
          placement: 'bottomLeft'
        })
      } catch (e) {
        Notification.error({ message: JSON.stringify(e), placement: 'bottomLeft' })
      }
    }
  }

  useEffect(() => {
    refetchCustomers()
    employeesSetQueryParamValues([{ key: 'roles', value: ['Employee'] }])
  }, [])

  useEffect(() => {
    console.log('EditableItem', editableItem)
  }, [editableItem])

  const setEditableAssignments = (assignments: CustomerAssignment[]) => {
    if (editableItem) {
      const classStripped = JSON.parse(JSON.stringify(assignments))
      form.setFields([{ name: 'assignments', value: classStripped }])
      setEditableItem({ ...editableItem, assignments: classStripped })
    }
  }

  const setEditableEmployees = (employees: CustomerEmployee[]) => {
    if (editableItem) {
      const classStripped = JSON.parse(JSON.stringify(employees))
      form.setFields([{ name: 'employees', value: classStripped }])
      setEditableItem({ ...editableItem, employees: classStripped })
    }
  }

  const setEditableContacts = (contacts: CustomerAssignment[]) => {
    if (editableItem) {
      const classStripped = JSON.parse(JSON.stringify(contacts))
      console.log('Contact Form ok', contacts)
      form.setFields([{ name: 'contacts', value: classStripped }])
      setEditableItem({ ...editableItem, contacts: classStripped })
    }
  }

  const capitalizeFirstLetter = (string: string | undefined) => {
    if (typeof string === 'string') {
      return string.charAt(0).toUpperCase() + string.slice(1)
    }
    return
  }

  const findEmployee = (_id: string) => {
    const found = employees?.find((item) => item._id === _id)
    if (!found) {
      return 'Tuntematon käyttäjä'
    }

    const name = `${found.last_name || ''} ${found.first_name || ''}`.trim()
    return name.length > 0 ? name : 'Tuntematon käyttäjä'
  }

  return (
    <>
      {/* Add Customer Modal */}
      <AddCustomerModal
        visible={showAddCustomerModal}
        onCancel={() => setShowAddCustomerModal(false)}
        onSave={(customer) => {
          setShowAddCustomerModal(false)
          createCustomer(customer)
        }}
      />
      {/* ModifyContacts */}
      <ModifyContactsModal
        data={editableItem?.contacts}
        visible={showModifyContacts}
        onCancel={() => setShowModifyContacts(false)}
        onSave={(contacts) => {
          setEditableContacts(contacts)
          setShowModifyContacts(false)
        }}
      />
      {/* ModifyContacts */}
      <ModifyAssignmentsModal
        data={editableItem?.assignments}
        visible={showModifyAssignments}
        onCancel={() => setShowModifyAssignments(false)}
        onSave={(assignments) => {
          setEditableAssignments(assignments)
          setShowModifyAssignments(false)
        }}
      />
      {/* ModifyEmployees */}
      <ModifyEmployees
        data={editableItem?.employees}
        employees={employees}
        visible={showModifyEmployees}
        editableItem={editableItem}
        onCancel={() => setShowModifyEmployees(false)}
        onSave={(items) => {
          setEditableEmployees(items)
          setShowModifyEmployees(false)
        }}
      />
      {/* Content */}
      <Content
        contentScroll={true}
        contentDisablePadding={true}
        contentStyle={{ justifyContent: 'center' }}
        toolbarTheme={'white-shadow'}
        loaderTheme={'transparent'}
        toolbarLeft={
          <h1>
            {global.Translations.get('customers')} ({customers?.length})
          </h1>
        }
        loading={(customersLoading || employeesLoading) && global.Translations.get('customers')}
        toolbarRight={
          <></>
          // <Button
          //   className="s-hide-on-print"
          //   label={'Lisää ' + global.Translations.get('customer').toLowerCase()}
          //   icon={<i className="fas fa-plus"></i>}
          //   color="green"
          //   onClick={() => {
          //     setShowAddCustomerModal(true)
          //   }}
          // />
        }
        fullAbsolute={true}
      >
        <Button
          color="green"
          rounded={true}
          style={{ position: 'fixed', right: '1em', bottom: '1em', zIndex: 1000 }}
          label={<i className="fa-solid fa-plus"></i>}
          onClick={() => {
            setShowAddCustomerModal(true)
          }}
        />
        {/* Form Wrapper */}
        <Form
          form={form}
          name="basic"
          initialValues={{ remember: true }}
          onFinish={async (value: Customer) => {
            try {
              const validated = new Customer(value)
              setEditableItem(validated)
              handleUpdateCustomer()
            } catch (e) {
              Notification.error({ message: JSON.stringify(e) })
            }
          }}
          onFinishFailed={() => {}}
          autoComplete="off"
        >
          {/* Table */}
          <Table
            thead={
              <Tr>
                <Th _id="name" style={{ width: '200px' }}>
                  {global.Translations.get('customer')}
                </Th>

                <Th _id="city" style={{ width: '200px' }}>
                  Kaupunki
                </Th>

                <Th _id="contacts">Yhteyshenkilöt</Th>
                <Th _id="tes_name" style={{ width: '200px' }}>
                  TES
                </Th>
                <Th _id="assignments">Toimeksiannot</Th>
                <Th _id="employee_ids" style={{ width: '300px' }}>
                  Työntekijät
                </Th>
                <Th _id="buttons" style={{ width: '200px' }} className="s-hide-on-print"></Th>
              </Tr>
            }
            tbody={
              <>
                {customers?.map((item, index) => {
                  if (editableItem && editableItem._id === item._id) {
                    // EDITABLE TR
                    return (
                      <Tr style={{ background: 'rgba(32, 185, 37, 0.62)' }}>
                        <Td>
                          <div>
                            <Form.Item
                              hidden={true}
                              style={{ marginBottom: '0px' }}
                              initialValue={editableItem._id}
                              name="_id"
                              rules={[{ required: true }]}
                            >
                              <Input />
                            </Form.Item>
                            <Form.Item
                              style={{ marginBottom: '0px' }}
                              initialValue={editableItem.name}
                              name="name"
                              rules={[{ required: true, message: 'Anna Nimi' }]}
                            >
                              <Input placeholder="Nimi" />
                            </Form.Item>
                            <Form.Item
                              style={{ marginBottom: '0px' }}
                              initialValue={editableItem.business_identity_code}
                              name="business_identity_code"
                            >
                              <Input />
                            </Form.Item>
                          </div>
                        </Td>

                        <Td>
                          <Form.Item
                            style={{ marginBottom: '0px' }}
                            initialValue={editableItem.city}
                            name="city"
                            rules={[{ required: true }]}
                            normalize={(value: string) => {
                              if (value) {
                                return value
                              } else {
                                return ''
                              }
                            }}
                          >
                            <Select placeholder="Kaupunki">
                              <Select.Option value={'pori'}>Pori</Select.Option>
                              <Select.Option value={'turku'}>Turku</Select.Option>
                              <Select.Option value={'espoo'}>Espoo</Select.Option>
                            </Select>
                          </Form.Item>
                        </Td>

                        <Td>
                          <Form.Item initialValue={editableItem.contacts} style={{ width: '100%' }} name="contacts">
                            <div
                              className="c-customer-own-form-wrapper"
                              onClick={() => {
                                setShowModifyContacts(true)
                              }}
                            >
                              {editableItem.contacts.map((contact) => {
                                return <DisplayCustomerContact contact={contact} />
                              })}
                              {editableItem.contacts.length === 0 && <span style={{ color: 'rgba(5,0,0,0.3)' }}>Yhteystiedot</span>}
                            </div>
                          </Form.Item>
                        </Td>
                        <Td>
                          <Form.Item style={{ marginBottom: '0px' }} initialValue={editableItem.tes_name} name="tes_name">
                            <Input />
                          </Form.Item>
                        </Td>
                        <Td>
                          <Form.Item initialValue={editableItem.assignments} style={{ width: '100%' }} name="assignments">
                            <div
                              className="c-customer-own-form-wrapper"
                              onClick={() => {
                                setShowModifyAssignments(true)
                              }}
                            >
                              {editableItem.assignments.map((assignment) => {
                                return <DisplayCustomerAssignment data={assignment} />
                              })}
                              {editableItem.contacts.length === 0 && <span style={{ color: 'rgba(5,0,0,0.3)' }}>Toimeksiannot</span>}
                            </div>
                          </Form.Item>
                        </Td>
                        <Td>
                          <Form.Item initialValue={editableItem.employees} style={{ width: '100%' }} name="employees">
                            <div
                              className="c-customer-own-form-wrapper"
                              onClick={() => {
                                setShowModifyEmployees(true)
                              }}
                            >
                              {editableItem.employees.map((employee) => {
                                return (
                                  <div style={{ display: 'flex', width: '100%' }}>
                                    <div style={{ flex: 1 }}>
                                      {findEmployee(employee._id)}
                                      {employee.assignment_ids.length > 0 && ` (${employee.assignment_ids.length})`}
                                    </div>
                                    {employee.hour_rate && <div>{`${employee.hour_rate} €/h`}</div>}
                                  </div>
                                )
                              })}
                              {editableItem.employees.length === 0 && <span style={{ color: 'rgba(5,0,0,0.3)' }}>Työntekijät</span>}
                            </div>
                          </Form.Item>
                        </Td>
                        <Td className="s-hide-on-print">
                          {/* EDITABLE TR BUTTONS */}
                          <Button.ButtonGroup margin="m" style={{} || { position: 'fixed', bottom: '1em', right: '2em' }}>
                            <Button
                              label="Peruuta"
                              icon={<i className="fas fa-history"></i>}
                              onClick={() => {
                                setEditableItem(undefined)
                                form.resetFields()
                              }}
                              color="default"
                            />
                            <Button label="Tallenna" type="submit" icon={<i className="far fa-save"></i>} onClick={() => {}} color="default" />
                          </Button.ButtonGroup>
                        </Td>
                      </Tr>
                    )
                  } else {
                    // STATIC TR
                    return (
                      <Tr>
                        <Td>
                          <div>
                            <div style={{ fontWeight: 400 }}>{item.name}</div>
                            <div>{item.business_identity_code}</div>
                          </div>
                        </Td>

                        <Td>{capitalizeFirstLetter(item.city)}</Td>

                        <Td>
                          <div>
                            {item.contacts.map((contact) => {
                              return <DisplayCustomerContact contact={contact} />
                            })}
                          </div>
                        </Td>
                        <Td>{item.tes_name}</Td>
                        <Td>
                          <div>
                            {item.assignments.map((assignment) => {
                              return <DisplayCustomerAssignment data={assignment} />
                            })}
                          </div>
                        </Td>
                        <Td>
                          <div style={{ width: '100%' }}>
                            {item.employees.map((employee) => {
                              return (
                                // <a href={`/employee-monthly-reports?employeeId=${employee._id}&customerId=${item._id}`}>
                                <div style={{ display: 'flex', width: '100%' }}>
                                  <div style={{ flex: 1 }}>
                                    {findEmployee(employee._id)}
                                    {employee.assignment_ids.length > 0 && ` (${employee.assignment_ids.length})`}
                                  </div>
                                  {employee.hour_rate && <div>{`${employee.hour_rate} €/h`}</div>}
                                </div>
                                // </a>
                              )
                            })}
                          </div>
                        </Td>
                        <Td className="s-hide-on-print">
                          {/* STATIC TR BUTTONS */}
                          <Button.ButtonGroup margin="m" style={editableItem ? { opacity: 0.1 } : {}}>
                            <Button
                              icon={<i className="far fa-edit"></i>}
                              onClick={() => {
                                setEditableItem(item)
                                form.resetFields()
                              }}
                              color="default"
                              disabled={editableItem ? true : false}
                            />
                            {/* <Button
                              icon={<i className="fas fa-chart-bar"></i>}
                              onClick={() => {
                                history.push(`/employee-monthly-reports?customerId=${item._id}`)
                              }}
                              color="default"
                              disabled={editableItem ? true : false}
                            /> */}
                            <PopConfirm
                              title="Haluatko varmasti poistaa tämän asiakkuuden？"
                              okText="Kyllä"
                              cancelText="En"
                              onConfirm={() => deleteCustomer(item._id)}
                              disabled={editableItem ? true : false}
                            >
                              <Button
                                icon={<i className="far fa-trash-alt"></i>}
                                onClick={() => {}}
                                color="default"
                                disabled={editableItem ? true : false}
                              />
                            </PopConfirm>
                          </Button.ButtonGroup>
                        </Td>
                      </Tr>
                    )
                  }
                })}
                {!employeesLoading && customers?.length === 0 && (
                  <tr>
                    <td colSpan={10}>
                      <div style={{ background: 'rgba(5,0,0,0.05  )', display: 'flex', justifyContent: 'center' }}>Ei vielä asiakkaita</div>
                    </td>
                  </tr>
                )}
              </>
            }
          />
          <div style={{ height: '400px' }}></div>
        </Form>
      </Content>
    </>
  )
}

export default Customers
